import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CssBaseline from '@mui/material/CssBaseline';
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Menu,
    Tooltip
} from "@mui/material";
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import Brightness4TwoToneIcon from '@mui/icons-material/Brightness4TwoTone';
import Brightness5TwoToneIcon from '@mui/icons-material/Brightness5TwoTone';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import AssignmentTwoToneIcon from "@mui/icons-material/AssignmentTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import QueryStatsTwoToneIcon from "@mui/icons-material/QueryStatsTwoTone";
import VpnKeyTwoToneIcon from "@mui/icons-material/VpnKeyTwoTone";
import LiveHelpTwoToneIcon from "@mui/icons-material/LiveHelpTwoTone";
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import AutoAwesomeTwoToneIcon from '@mui/icons-material/AutoAwesomeTwoTone';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import DynamicFormTwoToneIcon from '@mui/icons-material/DynamicFormTwoTone';
import FaceTwoToneIcon from '@mui/icons-material/FaceTwoTone';
import {HeaderStyle} from "../../../styles/components/HeaderStyle";
import {setThemeType} from "../../../services/AppServices";
import {AppContext} from "../../../App";
import logo from "../../../assets/v2/atlantis_logo.svg";
import logoCompleanno from "../../../assets/v2/557birthdaycake1_100853.svg";
import {ChangePasswordDialog} from "../../auth/utils/ChangePasswordDialog";
import Profile from "./Profile";
import {OCEAN__MAIN_GLOBAL_THEMA_TEXT, PROFILE_TEXT,} from "../../../strings/IT";
import {getAccountInfo} from "../../../utils/GenericUtils";
import moment from "moment";
import {BaseStyle} from "../../../styles/shared/BaseStyle";
import clsx from "clsx";
import multiavatar from "@multiavatar/multiavatar";
import SVG from "react-inlinesvg";
import {useHistory} from "react-router-dom";

const menuSections = [
    {
        title: "Operativita",
        items: [
            {
                name: "Richieste",
                description: "Ticket e segnalazioni",
                url: "/gestione/tickets",
                color: "#4285F4",
                color2: "#00A8E8",
                icon: <AssignmentTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_OPERATOR"],
            },
            {
                name: "Anagrafica",
                description: "Ricerca pazienti",
                url: "/gestione/pazienti",
                color: "#34A853",
                color2: "#00A8A8",
                icon: <SearchTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_SP_301", "ROLE_SP_301_H"],
            },
            {
                name: "Agende",
                description: "Appuntamenti",
                url: "/gestione/prenotazioni",
                color: "#FBBC05",
                color2: "#F57C00",
                icon: <EventAvailableTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_SP_301"],
            },
        ],
    },
    {
        title: "Analisi",
        items: [
            {
                name: "Report",
                description: "Grafici e KPI",
                url: "/gestione/report",
                color: "#7E57C2",
                color2: "#4285F4",
                icon: <QueryStatsTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_DELEGATO", "ROLE_REFERENTE", "ROLE_COMPANY", "ROLE_GRAFICI"],
            },
            {
                name: "FAQ",
                description: "Domande e risposte",
                url: "/gestione/faq",
                color: "#EA4335",
                color2: "#FBBC05",
                icon: <LiveHelpTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_SP_301"],
            },
            {
                name: "Azienda",
                description: "Referenti",
                url: "/gestione/azienda",
                color: "#009688",
                color2: "#34A853",
                icon: <VpnKeyTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_COMPANY", "ROLE_DELEGATO"],
            },
        ],
    },
    {
        title: "Configurazione",
        items: [
            {
                name: "Utenti",
                description: "Permessi",
                url: "/gestione/utenti",
                color: "#F9AB00",
                color2: "#EA4335",
                icon: <FaceTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN"],
            },
            {
                name: "Moduli",
                description: "Form dinamici",
                url: "/gestione/moduli",
                color: "#1A73E8",
                color2: "#7E57C2",
                icon: <DynamicFormTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_SP_301"],
            },
            {
                name: "Categorie",
                description: "Cataloghi",
                url: "/gestione/categorie",
                color: "#00ACC1",
                color2: "#34A853",
                icon: <CategoryRoundedIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_SP_301"],
            },
            {
                name: "Impostazioni",
                description: "Account e IA",
                url: "/gestione/settings",
                color: "#304FFE",
                color2: "#00A8A8",
                icon: <AutoAwesomeTwoToneIcon/>,
                roles: ["ROLE_SUPERADMIN", "ROLE_SP_301"],
            },
        ],
    },
];

const HideAppBar = ({
                        back = false,
                        title = "",
                        backPosition,
                        home = "/gestione",
                        goHome = true,
                        limited = false,
                        backFunction,
                        backOk = true,
                        useBackOk = false
                    }, ...props) => {
    const classes = HeaderStyle();
    const baseClasses = BaseStyle();
    const appContext = useContext(AppContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openChangePassword, setOpenChangePassword] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [appsAnchorEl, setAppsAnchorEl] = useState(null);
    const history = useHistory();
    const account = getAccountInfo() || {};

    const visibleSections = useMemo(() => {
        const roles = account.roles || [];

        return menuSections
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => !item.roles || roles.some((role) => item.roles.includes(role))),
            }))
            .filter((section) => section.items.length > 0);
    }, [account.roles]);

    useEffect(() => {
        document.body.style = "";
    }, []);

    const handleOpenAppsMenu = useCallback((event) => {
        setAppsAnchorEl(event.currentTarget);
    }, []);

    const handleCloseAppsMenu = useCallback(() => {
        setAppsAnchorEl(null);
    }, []);

    const handleGoBack = useCallback(() => {
        if (backPosition) {
            history.push(backPosition);
        } else {
            if (backFunction) {
                backFunction();
            }
            if (backOk) {
                history.goBack();
            }
        }
    }, [history, backPosition, backFunction, backOk]);

    const handleNavigate = useCallback((url) => {
        handleCloseAppsMenu();
        if (goHome) {
            history.push(url);
        } else {
            setOpenDialog(true);
        }
    }, [goHome, handleCloseAppsMenu, history]);

    const handleBrandClick = useCallback(() => {
        if (goHome) {
            history.push(home);
        } else {
            setOpenDialog(true);
        }
    }, [goHome, history, home]);

    useEffect(() => {
        if (backOk && useBackOk) {
            if (backPosition) {
                history.push(backPosition);
            } else {
                history.goBack();
            }
        }
    }, [backOk, useBackOk, history, backPosition]);

    return (
        <React.Fragment>
            {/*<CssBaseline/>*/}
            <AppBar position="fixed" elevation={0} className={classes.appbar}>
                <Toolbar className={classes.toolbar}>
                    <Box className={classes.brandZone}>
                        {back && (
                            <IconButton
                                edge="start"
                                className={clsx(classes.iconButton, classes.backButton)}
                                color="inherit"
                                aria-label="Indietro"
                                onClick={() => {
                                    goHome ? handleGoBack() : setOpenDialog(true);
                                }}
                                size="large">
                                <ArrowBackTwoToneIcon/>
                            </IconButton>
                        )}

                        <Tooltip classes={{tooltip: classes.tooltip}} title="Vai alla homepage">
                            <Box component="button" type="button" className={classes.brandButton} onClick={handleBrandClick}>
                                <img
                                    className={classes.logo}
                                    src={moment(new Date()).format("DD-MM-YYYY") === "17-03-2025"
                                        ? logoCompleanno
                                        : logo}
                                    alt="Atlantis"
                                />
                                <span className={classes.brandCopy}>
                                    <span className={classes.brandName}>Atlantis</span>
                                    <span className={classes.brandMeta}>Workspace</span>
                                </span>
                            </Box>
                        </Tooltip>
                    </Box>

                    {
                        (typeof title === 'string' || title instanceof String) ? (
                            <Typography className={classes.managerTitle} variant="h6">
                                {title || "Dashboard"}
                            </Typography>
                        ) : title
                    }

                    <Box className={classes.actionCluster}>
                        <Tooltip title="Apri menu applicazioni">
                            <IconButton className={classes.appsButton} onClick={handleOpenAppsMenu} size="large">
                                <AppsRoundedIcon/>
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={appsAnchorEl}
                            open={Boolean(appsAnchorEl)}
                            onClose={handleCloseAppsMenu}
                            keepMounted
                            getContentAnchorEl={null}
                            classes={{paper: classes.appsMenuPaper}}
                            anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                            transformOrigin={{vertical: "top", horizontal: "right"}}
                            PaperProps={{className: classes.appsMenuPaper, elevation: 0}}
                        >
                            <Box className={classes.appsPanel}>
                                <Box className={classes.appsHeader}>
                                    <Box>
                                        <Typography className={classes.appsEyebrow}>Menu</Typography>
                                        <Typography className={classes.appsTitle}>Strumenti Atlantis</Typography>
                                    </Box>
                                </Box>

                                {visibleSections.map((section) => (
                                    <Box className={classes.appsSection} key={section.title}>
                                        <Typography className={classes.appsSectionTitle}>{section.title}</Typography>
                                        <Box className={classes.appsGrid}>
                                            {section.items.map((item) => (
                                                <Box
                                                    key={item.url}
                                                    role="button"
                                                    tabIndex={0}
                                                    className={classes.appTile}
                                                    style={{
                                                        "--tile-color": item.color,
                                                        "--tile-color-2": item.color2,
                                                    }}
                                                    onClick={() => handleNavigate(item.url)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            handleNavigate(item.url);
                                                        }
                                                    }}
												>
													<Box className={classes.appTileIcon}>{item.icon}</Box>
													<Box className={classes.appTileCopy}>
														<Typography className={classes.appTileTitle}>{item.name}</Typography>
														<Typography className={classes.appTileDescription}>{item.description}</Typography>
													</Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Menu>

                        <Tooltip title={OCEAN__MAIN_GLOBAL_THEMA_TEXT}>
                            <IconButton
                                className={classes.iconButton}
                                color="inherit"
                                onClick={() =>
                                    setThemeType(
                                        appContext.state.theme.palette.mode === "light" ? "dark" : "light",
                                        appContext.state.theme,
                                        appContext.dispatch
                                    )
                                }
                                size="large">
                                {appContext.state.theme.palette.mode === "light"
                                    ? <Brightness4TwoToneIcon/>
                                    : <Brightness5TwoToneIcon/>
                                }
                            </IconButton>
                        </Tooltip>

                        {!limited && (
                            <Tooltip title={PROFILE_TEXT}>
                                <IconButton
                                    className={clsx(classes.iconButton, classes.profileButton)}
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                    color="inherit"
                                    size="large">
                                    <Avatar className={classes.avatarSmall}>
                                        <SVG src={multiavatar(account.username || "atlantis")}/>
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Toolbar>

                {props.isTabs && props.tabs}
            </AppBar>

            <Toolbar className={classes.toolbarOffset}/>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                PaperProps={{
                    className: clsx(baseClasses.roundBorder)
                }}
            >
                <DialogTitle>Attenzione</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Si prega di gestire il ticket oppure di salvare le modifiche premendo sul tasto "salva".
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {!limited && (
                <Profile
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    handleClose={setAnchorEl}
                    handleOpenChangePassword={() => setOpenChangePassword(true)}
                />
            )}

            <ChangePasswordDialog
                open={openChangePassword}
                handleClose={() => setOpenChangePassword(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(HideAppBar);
